import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useAuth } from '@/hooks/use-auth';
import type { Database } from '@/integrations/supabase/types';
import { type BrandVoiceWithDefaults, brandVoiceService } from '@/services/data';
import { showErrorToast, showSuccessToast } from '@/utils/toast';

// Map database type to hook interface
type BrandVoiceRow = Database['public']['Tables']['user_brand_voices']['Row'];
type BrandVoiceInsert = Database['public']['Tables']['user_brand_voices']['Insert'];
type BrandVoiceUpdate = Database['public']['Tables']['user_brand_voices']['Update'];

export interface BrandVoice {
  id: string;
  profile_name: string;
  tone_descriptors: string[];
  target_audience_summary: string;
  core_values: string[];
  keywords_to_include: string[];
  keywords_to_avoid: string[];
  is_default: boolean;
  created_at: string;
  brand_kit_id?: string | null;
}

export interface BrandVoiceSelection {
  voice: BrandVoice;
  reasoning?: string;
}

// Helper function to transform database row to BrandVoice interface
const transformBrandVoice = (voice: BrandVoiceRow | BrandVoiceWithDefaults): BrandVoice => {
  // Extract tone descriptors from the tone field
  const toneDescriptors = voice.tone ? voice.tone.split(',').map((t) => t.trim()) : [];

  return {
    id: voice.id,
    profile_name: voice.profile_name || 'Unnamed Profile',
    tone_descriptors: toneDescriptors,
    target_audience_summary: voice.audience_description || '',
    core_values: voice.keywords || [],
    keywords_to_include: voice.keywords || [],
    keywords_to_avoid: voice.avoid_phrases || [],
    is_default: 'is_default' in voice ? voice.is_default || false : false,
    created_at: voice.created_at,
    brand_kit_id: voice.brand_kit_id ?? null,
  };
};

interface UseBrandVoicesReturn {
  brandVoices: BrandVoice[];
  selectedVoice: BrandVoice | null;
  isLoading: boolean;
  error: string | null;
  selectBrandVoice: (voiceId: string, reasoning?: string) => BrandVoiceSelection | null;
  getBrandVoiceContext: () => {
    availableVoices: Array<{
      id: string;
      name: string;
      tone: string[];
      description: string;
    }>;
    selectedVoice: string | undefined;
    suggestedVoice: string | undefined;
    reasoning: string | undefined;
  };
  getContentContext: (voiceId?: string) => {
    brandVoice: string;
    tone: string;
    targetAudience: string;
    coreValues: string[];
    includeKeywords: string[];
    avoidKeywords: string[];
    guidelines: string;
  } | null;
  generateVoiceOptions: () => Array<{
    id: string;
    label: string;
    description: string;
    tone: string[];
    audience: string;
  }>;
  hasBrandVoices: boolean;
  refetch: () => unknown;
  invalidateCache: () => void;
  createBrandVoice: (
    voice: Omit<BrandVoiceInsert, 'user_id'>,
  ) => Promise<BrandVoiceRow | null | undefined>;
  updateBrandVoice: (
    id: string,
    updates: BrandVoiceUpdate,
  ) => Promise<BrandVoiceRow | null | undefined>;
  deleteBrandVoice: (id: string) => Promise<void>;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

export const useBrandVoices = (): UseBrandVoicesReturn => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedVoice, setSelectedVoice] = useState<BrandVoice | null>(null);

  // Use React Query for data fetching
  const {
    data: brandVoicesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['brand-voices', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      const response = await brandVoiceService.getBrandVoices();
      if (response.error) {
        throw new Error(response.error.message);
      }

      return response.data || [];
    },
    enabled: !!user?.id && !!user.id,
    retry: false,
  });

  // Transform the data with memoization
  const brandVoices = useMemo(
    () => brandVoicesData?.map(transformBrandVoice) || [],
    [brandVoicesData],
  );

  // Auto-select default voice or first voice
  useEffect(() => {
    if (brandVoices.length > 0 && !selectedVoice) {
      const defaultVoice = brandVoices.find((v) => v.is_default) || brandVoices[0];
      if (defaultVoice) {
        setSelectedVoice(defaultVoice);
      }
    }
  }, [brandVoices, selectedVoice]);

  // Select a specific brand voice
  const selectBrandVoice = useCallback(
    (voiceId: string, reasoning?: string) => {
      const voice = brandVoices.find((v) => v.id === voiceId);
      if (voice) {
        setSelectedVoice(voice);
        return { voice, reasoning };
      }
      return null;
    },
    [brandVoices],
  );

  // Get brand voice context for AI
  const getBrandVoiceContext = useCallback(() => {
    return {
      availableVoices: brandVoices.map((voice) => ({
        id: voice.id,
        name: voice.profile_name,
        tone: voice.tone_descriptors,
        description: voice.target_audience_summary,
      })),
      selectedVoice: selectedVoice?.id,
      suggestedVoice: brandVoices.find((v) => v.is_default)?.id || brandVoices[0]?.id,
      reasoning: selectedVoice ? `Using "${selectedVoice.profile_name}" brand voice` : undefined,
    };
  }, [brandVoices, selectedVoice]);

  // Get content generation context
  const getContentContext = useCallback(
    (voiceId?: string) => {
      const voice = voiceId ? brandVoices.find((v) => v.id === voiceId) : selectedVoice;
      if (!voice) return null;

      return {
        brandVoice: voice.profile_name,
        tone: voice.tone_descriptors.join(', '),
        targetAudience: voice.target_audience_summary,
        coreValues: voice.core_values,
        includeKeywords: voice.keywords_to_include,
        avoidKeywords: voice.keywords_to_avoid,
        guidelines: `Write in a ${voice.tone_descriptors.join(', ')} tone for ${voice.target_audience_summary}. Include these values: ${voice.core_values.join(', ')}.`,
      };
    },
    [brandVoices, selectedVoice],
  );

  // Generate brand voice options for AI suggestions
  const generateVoiceOptions = useCallback(() => {
    return brandVoices.map((voice) => ({
      id: voice.id,
      label: voice.profile_name,
      description: `${voice.tone_descriptors.join(', ')} tone for ${voice.target_audience_summary}`,
      tone: voice.tone_descriptors,
      audience: voice.target_audience_summary,
    }));
  }, [brandVoices]);

  // Invalidate cache to force refetch
  const invalidateCache = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['brand-voices', user?.id] });
  }, [queryClient, user?.id]);

  // Create brand voice mutation
  const createBrandVoiceMutation = useMutation({
    mutationFn: async (voice: Omit<BrandVoiceInsert, 'user_id'>) => {
      const response = await brandVoiceService.createBrandVoice(voice);
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['brand-voices', user?.id] });
      showSuccessToast('Success', `Profile "${data?.profile_name || ''}" created.`);
    },
    onError: (error) => {
      showErrorToast(
        'Error',
        error instanceof Error ? error.message : 'Failed to create brand voice',
      );
    },
  });

  // Update brand voice mutation
  const updateBrandVoiceMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: BrandVoiceUpdate }) => {
      const response = await brandVoiceService.updateBrandVoice(id, updates);
      if (response.error) throw response.error;
      return response.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['brand-voices', user?.id] });
      showSuccessToast('Success', `Profile "${data?.profile_name || ''}" updated.`);
    },
    onError: (error) => {
      showErrorToast(
        'Error',
        error instanceof Error ? error.message : 'Failed to update brand voice',
      );
    },
  });

  // Delete brand voice mutation
  const deleteBrandVoiceMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await brandVoiceService.deleteBrandVoice(id);
      if (response.error) throw response.error;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['brand-voices', user?.id] });
      showSuccessToast('Success', 'Profile deleted.');
    },
    onError: (error) => {
      showErrorToast(
        'Error',
        error instanceof Error ? error.message : 'Failed to delete brand voice',
      );
    },
  });

  // Helper functions for mutations
  const createBrandVoice = useCallback(
    async (voice: Omit<BrandVoiceInsert, 'user_id'>) => {
      return createBrandVoiceMutation.mutateAsync(voice);
    },
    [createBrandVoiceMutation],
  );

  const updateBrandVoice = useCallback(
    async (id: string, updates: BrandVoiceUpdate) => {
      return updateBrandVoiceMutation.mutateAsync({ id, updates });
    },
    [updateBrandVoiceMutation],
  );

  const deleteBrandVoice = useCallback(
    async (id: string) => {
      return deleteBrandVoiceMutation.mutateAsync(id);
    },
    [deleteBrandVoiceMutation],
  );

  return {
    brandVoices,
    selectedVoice,
    isLoading,
    error: error instanceof Error ? error.message : null,
    selectBrandVoice,
    getBrandVoiceContext,
    getContentContext,
    generateVoiceOptions,
    hasBrandVoices: brandVoices.length > 0,
    refetch,
    invalidateCache,
    // Mutations
    createBrandVoice,
    updateBrandVoice,
    deleteBrandVoice,
    isCreating: createBrandVoiceMutation.isPending,
    isUpdating: updateBrandVoiceMutation.isPending,
    isDeleting: deleteBrandVoiceMutation.isPending,
  };
};
